import { Component, HostBinding, Input, OnInit, afterNextRender } from '@angular/core';
import { NgIf } from '@angular/common';
import { HolidayClosure, HolidayClosureService } from '../../services/holiday-closure.service';

const DISMISSED_KEY = 'holiday-banner-dismissed';

/** Site-wide "we're closed" notice under the header. Placeholder styling until the
 *  designer's version lands; it only needs to read as S&F in the meantime.
 *  With `preview` set it renders that closure instead (the admin form's preview).
 *  Shows the header and description only: the closure's dates decide when it is up,
 *  and are not printed. */
@Component({
  selector: 'app-holiday-banner',
  imports: [NgIf],
  templateUrl: './holiday-banner.component.html',
  styleUrl: './holiday-banner.component.css'
})
export class HolidayBannerComponent implements OnInit {
  @Input() preview: Pick<HolidayClosure, 'title' | 'message'> | null = null;

  /** The closure the visitor closed this session. Read only after hydration: the
   *  server cannot see sessionStorage, so hiding in the first pass would not match
   *  the HTML it sent. Kept as an id so a different closure still shows. */
  private dismissedId = '';

  constructor(private closures: HolidayClosureService) {
    afterNextRender(() => {
      try {
        this.dismissedId = sessionStorage.getItem(DISMISSED_KEY) || '';
      } catch { }
    });
  }

  /** Browser only (the service skips the server), so cached page HTML never carries
   *  a banner. */
  ngOnInit(): void {
    if (!this.preview) this.closures.active();
  }

  /** Follows the service, so a closure published or stopped in the admin shows up
   *  (or goes) without a reload. */
  get closure(): HolidayClosure | null {
    return this.closures.current()[0] || null;
  }

  /** Lets page styles make room while a notice is up - see `.hb-on` in styles.css. */
  @HostBinding('class.hb-on') get on() {
    return !this.preview && !!this.shown;
  }

  get shown() {
    if (this.preview) return this.preview;
    const c = this.closure;
    return c && c.id !== this.dismissedId ? c : null;
  }

  dismiss() {
    const c = this.closure;
    if (!c) return;
    this.dismissedId = c.id;
    try {
      sessionStorage.setItem(DISMISSED_KEY, c.id);
    } catch { }
  }
}
